import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getSiteNotice } from '../api/siteNotice';
import {
  searchMyInvoices, fetchMyBillingSummary, submitPaymentProof,
} from '../api/companyBilling';
import { generateInvoicePdf } from '../utils/pdf';
import './PaymentInvoicePage.css';

// Asset du dossier public/ (pas src/) — CRA n'autorise pas les imports JS
// hors de src/, donc reference via PUBLIC_URL + style inline plutot qu'un
// import ou un url() CSS enracine (les deux echouent au build CRA).
const CGL_LOGO_URL = `${process.env.PUBLIC_URL}/logo-cgl-transparent.png`;

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return `${d}/${m}/${y}`;
};

// Liste reelle fournie par l'utilisateur (2026-09-18, "CGL COMPTE 2.docx") —
// doit rester synchronise avec billing/models.py:PAYMENT_METHOD_CHOICES
// (source de verite backend, la vraie validation se fait cote serveur).
const PAYMENT_METHODS = [
  { value: 'unibank_htg', label: 'UNIBANK (HTG)' },
  { value: 'unibank_usd', label: 'UNIBANK (USD)' },
  { value: 'sogebank_htg', label: 'SOGEBANK (HTG)' },
  { value: 'sogebank_usd', label: 'SOGEBANK (USD)' },
  { value: 'buh_htg', label: 'BUH (HTG)' },
  { value: 'buh_usd', label: 'BUH (USD)' },
  { value: 'bnc_htg', label: 'BNC (HTG)' },
  { value: 'moncash', label: 'MonCash' },
  { value: 'natcash', label: 'NatCash' },
];

// Comptes CGL affiches sur la page pour que la company sache ou deposer
// (meme source que PAYMENT_METHODS ci-dessus, "CGL COMPTE 2.docx"). Adresse
// commune a tous les comptes bancaires.
const CGL_ACCOUNTS_ADDRESS = '6, Rue Lissa Dorce, Delmas 33';
const CGL_BANK_ACCOUNTS = [
  { bank: 'UNIBANK', currency: 'HTG', account: '16310211675596', type: 'Compte Courant', swift: 'UBNKHTPP', beneficiary: 'CGL Multi Service' },
  { bank: 'UNIBANK', currency: 'USD', account: '16310221675606', type: 'Compte Courant', swift: 'UBNKHTPP', beneficiary: 'CGL Multi Service' },
  { bank: 'SOGEBANK', currency: 'HTG', account: '3506020159', type: 'Compte Courant', swift: 'SOGHHTPP', beneficiary: 'CGL Multi Services' },
  { bank: 'SOGEBANK', currency: 'USD', account: '3516013046', type: 'Compte Courant', swift: 'SOGHHTPP', beneficiary: 'CGL Multi Services' },
  { bank: 'BUH', currency: 'HTG', account: '25000004629', type: 'Compte Courant', swift: 'BUHEHTPP', beneficiary: 'Charles Choudelin' },
  { bank: 'BUH', currency: 'USD', account: '55000130485', type: 'Epargne', swift: 'BUHEHTPP', beneficiary: 'Charles Choudelin' },
  { bank: 'BNC', currency: 'HTG', account: '2710018414', type: 'Compte Epargne', swift: 'BNCHHTPPXXX', beneficiary: 'Charles Choudelin' },
];
const CGL_MOBILE_ACCOUNTS = [
  { label: 'MonCash Marchande', number: '+509 34558012', name: 'CGL Multi Services' },
  { label: 'NatCash', number: '+509 55216928', name: 'CGL Multi Services', extra: 'Code Agent : 242325' },
];

// Page ouverte dans un nouvel onglet depuis le bouton "Pay Online" du widget
// dashboard (OnlinePaymentWidget.jsx) — demande explicite 2026-09-18. Droite :
// apercu de la facture (style "facture proforma", logo CGL en fond, adresse
// CGL tiree du bloc Avis) ; gauche : formulaire de depot du justificatif de
// paiement (recu photo/PDF + methode utilisee), en attente de verification
// par le superadmin (page de revue superadmin = prochaine etape, pas encore
// construite). Le montant lui-meme n'est PAS modifie automatiquement par ce
// depot — juste une preuve soumise a validation humaine.
const PaymentInvoicePage = () => {
  const { invoiceId } = useParams();
  const { company } = useAuth();

  const [invoice, setInvoice] = useState(null);
  const [totalBalance, setTotalBalance] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [method, setMethod] = useState(PAYMENT_METHODS[0].value);
  const [amount, setAmount] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([searchMyInvoices(''), fetchMyBillingSummary(), getSiteNotice()])
      .then(([invoices, summary, siteNotice]) => {
        if (cancelled) return;
        const found = invoices.find((inv) => String(inv.id) === String(invoiceId));
        setInvoice(found || null);
        setTotalBalance(Number(summary.total_balance || 0));
        setNotice(siteNotice);
      })
      .catch(() => { if (!cancelled) setLoadError('Chargement de la facture impossible.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoiceId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!receiptFile) {
      setSubmitError('Ajoute une photo ou un PDF du reçu.');
      return;
    }
    if (!amount || Number.isNaN(Number(amount)) || Number(amount) <= 0) {
      setSubmitError('Indique le montant versé.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const formData = new FormData();
      formData.append('method', method);
      formData.append('amount', amount);
      formData.append('receipt', receiptFile);
      if (note.trim()) formData.append('note', note.trim());
      if (invoice) formData.append('invoice', invoice.id);
      await submitPaymentProof(formData);
      setSubmitted(true);
    } catch (err) {
      setSubmitError('Envoi impossible. Réessaie.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      await generateInvoicePdf({
        notice, company, invoice, totalBalance,
        filename: `facture-${invoice.id}.pdf`,
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <p className="pip-loading">Chargement...</p>
      </Layout>
    );
  }

  return (
    <Layout>
      <p className="pip-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <span className="bc-current">Paiement en ligne</span>
      </p>

      {loadError && <p className="pip-error">{loadError}</p>}

      <div className="pip-columns">
        <div className="pip-form-col">
          <div className="pip-card pip-accounts-card">
            <div className="pip-card-header">
              <span className="pip-card-title">Comptes CGL pour le dépôt</span>
            </div>
            <div className="pip-card-body">
              <p className="pip-accounts-address">{CGL_ACCOUNTS_ADDRESS}</p>
              <div className="pip-accounts-list">
                {CGL_BANK_ACCOUNTS.map((acc, i) => (
                  <div className="pip-account-item" key={i}>
                    <div className="pip-account-bank">{acc.bank} ({acc.currency})</div>
                    <div className="pip-account-row"><span>N° compte</span><span>{acc.account}</span></div>
                    <div className="pip-account-row"><span>Type</span><span>{acc.type}</span></div>
                    <div className="pip-account-row"><span>Swift</span><span>{acc.swift}</span></div>
                    <div className="pip-account-row"><span>Bénéficiaire</span><span>{acc.beneficiary}</span></div>
                  </div>
                ))}
                {CGL_MOBILE_ACCOUNTS.map((acc, i) => (
                  <div className="pip-account-item" key={`mobile-${i}`}>
                    <div className="pip-account-bank">{acc.label}</div>
                    <div className="pip-account-row"><span>Numéro</span><span>{acc.number}</span></div>
                    <div className="pip-account-row"><span>Nom du compte</span><span>{acc.name}</span></div>
                    {acc.extra && <div className="pip-account-extra">{acc.extra}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pip-card">
            <div className="pip-card-header">
              <span className="pip-card-title">Justificatif de paiement</span>
            </div>
            <div className="pip-card-body">
              {!invoice && (
                <p className="pip-hint">
                  Aucune facture trouvée pour cet identifiant — tu peux quand même déposer un reçu ci-dessous, il sera rattaché à ton dossier.
                </p>
              )}

              {submitted ? (
                <p className="pip-success">
                  Reçu envoyé. En attente de vérification par le superadmin.
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="pip-form">
                  <div className="pip-field">
                    <label className="pip-label">Méthode de paiement</label>
                    <select
                      className="pip-select"
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="pip-field">
                    <label className="pip-label">Montant versé</label>
                    <input
                      className="pip-select"
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="pip-field">
                    <label className="pip-label">Reçu (photo ou PDF)</label>
                    <input
                      className="pip-file"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                    />
                  </div>

                  <div className="pip-field">
                    <label className="pip-label">Note (optionnel)</label>
                    <textarea
                      className="pip-textarea"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Référence de transaction, précisions..."
                    />
                  </div>

                  {submitError && <p className="pip-error">{submitError}</p>}

                  <button type="submit" className="pip-submit-btn" disabled={submitting}>
                    {submitting ? 'Envoi...' : 'Envoyer'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="pip-invoice-col">
          <div className="pip-invoice">
            <div className="pip-invoice-watermark" style={{ backgroundImage: `url(${CGL_LOGO_URL})` }} />
            <div className="pip-invoice-content">
              <div className="pip-invoice-top">
                <div className="pip-invoice-from">
                  <strong>{notice?.center1_name || 'CGL Multi-services'}</strong>
                  {(notice?.center1_lines || '').split('\n').filter(Boolean).map((l, i) => (
                    <p key={i}>{l}</p>
                  ))}
                </div>
                <div className="pip-invoice-meta">
                  <div><span className="pip-meta-label">Facture N°</span><span>{invoice ? `IVC-${company?.id}-${invoice.id}` : '—'}</span></div>
                  <div><span className="pip-meta-label">Date</span><span>{fmtDate(invoice?.period_end)}</span></div>
                  <div><span className="pip-meta-label">Période</span><span>{fmtDate(invoice?.period_start)} → {fmtDate(invoice?.period_end)}</span></div>
                </div>
              </div>

              <div className="pip-invoice-billto">
                <span className="pip-billto-label">Facturé à</span>
                <strong>{company?.name}</strong>
                {company?.address && <p>{company.address}</p>}
                {company?.phone && <p>{company.phone}</p>}
              </div>

              <div className="pip-invoice-summary">
                <div className="pip-summary-row">
                  <span>Code company</span>
                  <span>{company?.code || '—'}</span>
                </div>
                {invoice?.total_bank_count != null && (
                  <div className="pip-summary-row">
                    <span>Nombre total de banks</span>
                    <span>{invoice.total_bank_count}</span>
                  </div>
                )}
                <div className="pip-summary-row">
                  <span>Nombre de banks facturables</span>
                  <span>{invoice ? invoice.billable_bank_count : '—'}</span>
                </div>
                <div className="pip-summary-row">
                  <span>Prix par bank</span>
                  <span>{invoice ? money(invoice.rate_per_bank) : '—'}</span>
                </div>
                <div className="pip-summary-row pip-summary-total">
                  <span>Montant facture</span>
                  <span>{invoice ? money(invoice.amount_due) : '—'}</span>
                </div>
                <div className="pip-summary-row">
                  <span>Montant reçu</span>
                  <span>{invoice ? money(invoice.amount_paid || 0) : '—'}</span>
                </div>
                <div className="pip-summary-row">
                  <span>Crédit appliqué</span>
                  <span>{invoice ? money(invoice.credit_applied || 0) : '—'}</span>
                </div>
                <div className="pip-summary-row">
                  <span>Crédit disponible</span>
                  <span>{invoice ? money(invoice.credit_available || 0) : '—'}</span>
                </div>
                <div className="pip-summary-row pip-summary-balance">
                  <span>Solde antérieur</span>
                  <span>{invoice ? money(Number(invoice.manual_balance || 0) + Number(invoice.carried_balance || 0)) : '—'}</span>
                </div>
                <div className="pip-summary-row pip-summary-total">
                  <span>Montant à payer</span>
                  <span>{invoice ? money(invoice.total_due ?? totalBalance ?? invoice.balance) : '—'}</span>
                </div>
              </div>

              <section className="pip-billable-pos">
                <h3>Liste des POS facturables</h3>
                {Array.isArray(invoice?.billable_pos) && invoice.billable_pos.length > 0 ? (
                  <div className="pip-pos-table-wrap">
                    <table className="pip-pos-table">
                      <thead>
                        <tr>
                          <th>POS</th>
                          <th>Nom</th>
                          <th>Date de qualification</th>
                          <th>Tickets valides</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.billable_pos.map((pos, index) => (
                          <tr key={`${pos.pos_number}-${index}`}>
                            <td>{pos.pos_number ?? '—'}</td>
                            <td>{pos.pos_name || '—'}</td>
                            <td>{fmtDate(pos.qualification_date)}</td>
                            <td>{pos.valid_ticket_count ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="pip-no-billable-pos">
                    Aucun POS facturable pour cette période.
                  </p>
                )}
              </section>

              <p className="pip-invoice-thanks">Merci</p>
            </div>
          </div>

          <button
            type="button"
            className="pip-download-btn"
            onClick={handleDownload}
            disabled={!invoice || downloading}
          >
            {downloading ? 'Génération...' : 'Télécharger la facture (PDF)'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentInvoicePage;
